import React, { useState, useEffect } from "react";

import PageLayout from "../components/PageLayout.jsx";
import { useApi } from "../services/ApiService";
import ExportCsvComponent from "../components/ExportCsv.jsx";
import { Link } from "react-router-dom";
import clsx from "clsx";

const daysAgo = (isoDate) => {
  if (!isoDate) return "—";
  const ms = Date.now() - new Date(isoDate).getTime();
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  return `${days} days ago`;
};

const getNestedValue = (obj, path) => path.split(".").reduce((o, k) => o?.[k], obj);

const sortObjectsByKey =
  (key, direction = "asc") =>
  (a, b) => {
    let aVal = getNestedValue(a, key) ?? "";
    let bVal = getNestedValue(b, key) ?? "";
    aVal = typeof aVal === "string" ? aVal.toLowerCase() : aVal;
    bVal = typeof bVal === "string" ? bVal.toLowerCase() : bVal;
    if (aVal < bVal) return direction === "asc" ? -1 : 1;
    if (aVal > bVal) return direction === "asc" ? 1 : -1;
    return 0;
  };

export default function ManageSite() {
  const api = useApi();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("userCreateDate");
  const [sortDirection, setSortDirection] = useState("desc");

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };

  const sortedUsers = [...users].sort(sortObjectsByKey(sortKey, sortDirection));

  const fetchAllUsers = async (signal) => {
    setLoading(true);
    setError(null);
    try {
      let token = undefined;
      let allUsers = [];

      do {
        const params = { limit: 50, ...(token && { token }) };
        const response = await api.usersGet(params, { signal });
        if (!response.success) break;
        allUsers = [...allUsers, ...(response.data?.users || [])];
        setUsers(allUsers);
        token = response.data?.token || null;
      } while (token);

      const adminResponse = await api.usersGet({ groupName: "admin-users" }, { signal });
      setAdmins(adminResponse.data?.users || []);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Error fetching users:", error);
        setError(error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    fetchAllUsers(controller.signal);
    return () => controller.abort();
  }, []); // eslint-disable-line

  const count = {
    users: users.length,
    terms: users.filter((u) => u?.custom?.terms === "true").length,
    confirmed: users.filter((u) => u?.userStatus === "CONFIRMED").length,
  };

  return (
    <PageLayout title="Admin Portal">
      <section aria-labelledby="section-heading rounded-lg">
        <div className="bg-opacity-50 relative rounded-lg bg-gray-300 p-4 dark:bg-gray-600">
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h2>
                Users
                {loading && (
                  <span className="relative ml-2 inline-flex h-3 w-3">
                    <span className="bg-proof-blue absolute inline-flex h-full w-full animate-ping rounded-full opacity-75"></span>
                    <span className="bg-proof-blue relative inline-flex h-3 w-3 rounded-full"></span>
                  </span>
                )}
              </h2>
              <p className="mt-2">A list of all the site users including their name, email, role and status.</p>
            </div>
            <div className="mt-4 flex items-center justify-center gap-2 sm:mt-0 sm:ml-16 sm:flex-none">
              <Link to="/terms" className="text-proof-blue hover:opacity-50">
                Terms
              </Link>
              <ExportCsvComponent data={users} />
            </div>
          </div>
          <div className="mt-2 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle lg:px-8">
                <table className="min-w-full divide-y divide-gray-600 dark:divide-gray-300">
                  <colgroup>
                    <col className="sm:w-3/12" />
                    <col className="sm:w-4/12" />
                    <col className="sm:w-1/12" />
                    <col className="sm:w-1/12" />
                    <col className="sm:w-1/12" />
                  </colgroup>
                  <thead className="bg-gray-400 dark:bg-gray-800">
                    <tr>
                      {[
                        { key: "name", label: "Name", first: true },
                        { key: "email", label: "Email" },
                        { key: "userCreateDate", label: "Created" },
                        { key: "custom.terms", label: `Terms (${count.terms})` },
                        { key: "userStatus", label: `Account (${count.confirmed}/${count.users})` },
                      ].map((col) => (
                        <th
                          key={col.key}
                          scope="col"
                          className={clsx(
                            "cursor-pointer text-left text-sm font-semibold text-nowrap select-none",
                            col.first ? "py-3.5 pr-3 pl-4 sm:pl-4" : "px-3 py-3.5",
                          )}
                          onClick={() => handleSort(col.key)}
                        >
                          <span>{col.label}</span>
                          <span className={"ml-1 text-gray-600"}>
                            {sortKey === col.key ? (sortDirection === "asc" ? "\u25B2" : "\u25BC") : "\u2316"}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-400 dark:divide-gray-200">
                    {loading && users.length === 0 && (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-4 pr-3 pl-4 text-center text-sm font-medium whitespace-nowrap sm:pl-4"
                        >
                          Loading... please wait
                        </td>
                      </tr>
                    )}
                    {error && (
                      <tr>
                        <td
                          colSpan={5}
                          className="py-4 pr-3 pl-4 text-center text-sm font-medium whitespace-nowrap sm:pl-4"
                        >
                          Error loading users: {`${error}`}
                        </td>
                      </tr>
                    )}
                    {sortedUsers.map((user) => {
                      const isAdmin = admins.find((a) => a.sub === user.sub);
                      return (
                        <tr key={user.sub}>
                          <td
                            className={clsx(
                              "py-4 pr-3 pl-4 text-sm font-medium whitespace-nowrap sm:pl-4",
                              isAdmin && "text-proof-yellow",
                            )}
                          >
                            {user.name}
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap">
                            {user.email} ({user.username})
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap" title={user.userCreateDate}>
                            {daysAgo(user.userCreateDate)}
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap">
                            {user?.custom?.terms === "true" ? "Yes" : "No"}
                          </td>
                          <td className="px-3 py-4 text-sm whitespace-nowrap">{user.userStatus}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
